import { detectConcordiumProvider } from '@concordium/browser-wallet-api-helpers';
import type { WalletApi } from '@concordium/browser-wallet-api-helpers';
import { apiFetch } from './api';

export class AuthError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'AuthError';
  }
}

interface AuthChallenge {
  nonce: string;
  timestamp: number;
  expires_in: number;
}

export interface SessionStatus {
  authenticated: boolean;
  account_address?: string;
  created_at?: string;
  expires_at?: string;
  last_activity?: string;
}

interface LoginResponse {
  success: boolean;
  session_expires_at: string;
}

interface ApiError {
  error: {
    code: string;
    message: string;
  };
}

/**
 * Detect and return the Concordium browser wallet provider.
 */
async function getWallet(): Promise<WalletApi> {
  try {
    return await detectConcordiumProvider(2000);
  } catch {
    throw new AuthError(
      'WALLET_NOT_FOUND',
      'Concordium Wallet extension not detected. Please install it to continue.'
    );
  }
}

/**
 * Flatten AccountTransactionSignature (Record<number, Record<number, string>>)
 * into a single hex string for the first credential/key pair.
 */
function extractSignatureHex(
  sig: Record<number, Record<number, string>>
): string {
  const credentialKeys = Object.keys(sig);
  if (credentialKeys.length === 0) {
    throw new AuthError('WALLET_ERROR', 'No credential signatures returned');
  }
  const firstCredential = sig[Number(credentialKeys[0])];
  const keyIndices = Object.keys(firstCredential);
  if (keyIndices.length === 0) {
    throw new AuthError('WALLET_ERROR', 'No key signatures returned');
  }
  return firstCredential[Number(keyIndices[0])];
}

/**
 * Full Concordium wallet login flow:
 * 1. Connect wallet and get account address
 * 2. Request challenge from Portal
 * 3. Sign challenge with wallet
 * 4. Submit signature to Portal for verification (TPPS verifies via Concordium node)
 */
export async function login(): Promise<{
  accountAddress: string;
  expiresAt: string;
}> {
  const wallet = await getWallet();

  // Step 1: Connect wallet and get account
  let accountAddress: string;
  try {
    // connect() opens the wallet popup so the user can approve the site
    const connectedAccount = await wallet.connect();
    if (connectedAccount) {
      accountAddress = connectedAccount;
    } else {
      // Fallback: try requestAccounts if connect didn't return an address
      const accounts = await wallet.requestAccounts();
      if (accounts.length > 0) {
        accountAddress = accounts[0];
      } else {
        const selected = await wallet.getMostRecentlySelectedAccount();
        if (!selected) {
          throw new AuthError(
            'NO_ACCOUNT',
            'No Concordium account available. Please create or connect an account in your wallet.'
          );
        }
        accountAddress = selected;
      }
    }
  } catch (e) {
    if (e instanceof AuthError) throw e;
    throw new AuthError('WALLET_ERROR', 'Failed to connect to wallet');
  }

  // Step 2: Request challenge from Portal
  const challengeRes = await apiFetch('/v1/auth/challenge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!challengeRes.ok) {
    throw new AuthError('CHALLENGE_FAILED', 'Failed to get authentication challenge from server');
  }

  const challenge: AuthChallenge = await challengeRes.json();

  // Step 3: Prepare and sign message
  const message = JSON.stringify({
    nonce: challenge.nonce,
    timestamp: challenge.timestamp,
    service: 'TPP Portal Login',
  });

  let signatureHex: string;
  try {
    const signResult = await wallet.signMessage(accountAddress, message);
    signatureHex = extractSignatureHex(signResult);
  } catch (e: unknown) {
    if (e instanceof AuthError) throw e;
    const walletError = e as { code?: number; message?: string };
    if (walletError.code === 4001) {
      throw new AuthError('USER_REJECTED', 'Signature request was rejected');
    }
    throw new AuthError(
      'WALLET_ERROR',
      walletError.message ?? 'Failed to sign message'
    );
  }

  // Step 4: Submit to Portal for verification
  // Portal delegates signature verification to TPPS, which queries the
  // Concordium node directly via gRPC to get account public keys.
  // We send the exact message string that was signed to avoid any
  // reconstruction mismatch on the backend.
  const loginRes = await apiFetch('/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      account_address: accountAddress,
      challenge: {
        nonce: challenge.nonce,
        timestamp: challenge.timestamp,
      },
      signature: signatureHex,
      message,
    }),
  });

  if (!loginRes.ok) {
    const errorBody: ApiError = await loginRes.json();
    throw new AuthError(errorBody.error.code, errorBody.error.message);
  }

  const loginData: LoginResponse = await loginRes.json();

  return {
    accountAddress,
    expiresAt: loginData.session_expires_at,
  };
}

/**
 * Destroy the current session on the server.
 */
export async function logout(): Promise<void> {
  await apiFetch('/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

/**
 * Check current session status with the server.
 */
export async function getSessionStatus(): Promise<SessionStatus> {
  const res = await apiFetch('/v1/auth/session', {
    credentials: 'include',
  });
  return res.json();
}

/**
 * Wrapper for authenticated API requests.
 * Automatically includes session cookie and handles 401 responses.
 */
export async function authenticatedFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const res = await apiFetch(path, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (res.status === 401) {
    throw new AuthError('SESSION_EXPIRED', 'Session expired');
  }

  return res;
}
